import loadable from '@loadable/component'
import { Navigate, useRoutes } from 'react-router-dom'
import UserStore from 'src/mobx/user'
import {
  PATH_ACTIVATE,
  PATH_COURSES,
  PATH_CV,
  PATH_DASHBOARD,
  PATH_DEPTS,
  PATH_DIARY,
  PATH_DICTIONARY,
  PATH_FINANCES,
  PATH_IMAGES,
  PATH_LOGIN,
  PATH_MATERIALS,
  PATH_NOTFOUND,
  PATH_PROFILE,
  PATH_REGISTER,
  PATH_RESTORE_PASSWORD,
  PATH_SETTINGS,
  PATH_TODO,
  PATH_USER,
  PATH_WORKOUT,
  ROUTE_COURSE_SHARED,
  ROUTE_DICTIONARY,
  ROUTE_I_MEMORY,
  ROUTE_I_REMEMBER,
  ROUTE_I_REMEMBER_SRS,
  ROUTE_LETTER_HAMMER,
  ROUTE_MATERIAL_SHARED,
  ROUTE_PRACTICE_SENTENCE_REVIEW,
  ROUTE_PUBLIC_DEPT,
  ROUTE_PUBLIC_DICTIONARY,
  ROUTE_PUBLIC_SHARED_TODO,
} from 'src/resources/routes'

import LogoOnlyLayout from './layouts/LogoOnlyLayout'
import ProtectedRoute from './layouts/ProtectedRoute'
import NotFound from './pages/404'
import Workouts from './pages/dashboard/workouts'

const DashboardLayout = loadable(() => import('./layouts/dashboard'))
const Dictionary = loadable(() => import('./pages/dashboard/Dictionary'))
const LetterHammer = loadable(
  () => import('src/pages/dashboard/workouts/LetterHammer'),
)
const IMemory = loadable(() => import('./pages/dashboard/workouts/iMemory'))
const IRememberSrs = loadable(() => import('./pages/dashboard/workouts/iRememberSrs'))
const IRemember = loadable(() => import('./pages/dashboard/workouts/iRemember'))
const PracticeSentenceReview = loadable(
  () => import('./pages/dashboard/workouts/PracticeSentenceReview'),
)
const CV = loadable(() => import('./pages/dashboard/CV'))
const Diary = loadable(() => import('./pages/dashboard/diary'))
const Finances = loadable(() => import('./pages/dashboard/Finances'))
const Depts = loadable(() => import('./pages/dashboard/Depts'))
const Materials = loadable(() => import('./pages/dashboard/Materials'))
const Images = loadable(() => import('./pages/dashboard/Images'))
const MaterialEditor = loadable(() => import('./pages/dashboard/MaterialEditor'))
const MaterialView = loadable(() => import('./pages/dashboard/MaterialView'))
const MaterialPlayer = loadable(() => import('./pages/MaterialPlayer'))
const Courses = loadable(() => import('./pages/dashboard/Courses'))
const CourseEditor = loadable(() => import('./pages/dashboard/CourseEditor'))
const CoursePlay = loadable(() => import('./pages/dashboard/CoursePlay'))
const CoursePlayerPublic = loadable(() => import('./pages/CoursePlayerPublic'))
const Login = loadable(() => import('./pages/Login'))
const Todo = loadable(() => import('./pages/dashboard/Todo'))
const Register = loadable(() => import('./pages/Register'))
const Activate = loadable(() => import('./pages/Activate'))
const PublicDictionary = loadable(() => import('./pages/PublicDictionary'))
const PublicDept = loadable(() => import('./pages/PublicDept'))
const PublicSharedTodo = loadable(() => import('./pages/PublicSharedTodo'))
const FinishPasswordRestoration = loadable(
  () => import('./pages/FinishPasswordRestoration'),
)
const StartPasswordRestoration = loadable(
  () => import('./pages/StartPasswordRestoration'),
)
const Profile = loadable(() => import('./pages/user/Profile'))
const Settings = loadable(() => import('./pages/user/Settings'))

export default function Router() {
  return useRoutes([
    {
      path: PATH_DASHBOARD,
      element: (
        <ProtectedRoute isAllowed={!!UserStore.user}>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        { path: PATH_DICTIONARY, element: <Dictionary /> },
        { path: PATH_WORKOUT, element: <Workouts /> },
        { path: ROUTE_I_REMEMBER, element: <IRemember /> },
        { path: ROUTE_I_REMEMBER_SRS, element: <IRememberSrs /> },
        {
          path: ROUTE_PRACTICE_SENTENCE_REVIEW,
          element: <PracticeSentenceReview />,
        },
        { path: ROUTE_I_MEMORY, element: <IMemory /> },
        { path: ROUTE_LETTER_HAMMER, element: <LetterHammer /> },
        { path: PATH_TODO, element: <Todo /> },
        { path: PATH_CV, element: <CV /> },
        { path: PATH_DIARY, element: <Diary /> },
        { path: PATH_FINANCES, element: <Finances /> },
        { path: PATH_DEPTS, element: <Depts /> },
        { path: PATH_MATERIALS, element: <Materials /> },
        { path: `${PATH_MATERIALS}/:id/edit`, element: <MaterialEditor /> },
        { path: `${PATH_MATERIALS}/:id/view`, element: <MaterialView /> },
        { path: PATH_COURSES, element: <Courses /> },
        { path: `${PATH_COURSES}/:id/edit`, element: <CourseEditor /> },
        { path: `${PATH_COURSES}/:id/play`, element: <CoursePlay /> },
        { path: PATH_IMAGES, element: <Images /> },
        { path: `${PATH_USER}/${PATH_PROFILE}`, element: <Profile /> },
        { path: `${PATH_USER}/${PATH_SETTINGS}`, element: <Settings /> },
      ],
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: '/', element: <Navigate to={ROUTE_DICTIONARY} /> },
        {
          path: PATH_LOGIN,
          element: <Login />,
        },
        { path: PATH_REGISTER, element: <Register /> },
        {
          path: `${PATH_ACTIVATE}/`,
          element: <Activate />,
          children: [{ path: ':link', element: <Activate /> }],
        },
        {
          path: PATH_RESTORE_PASSWORD,
          element: <StartPasswordRestoration />,
        },
        {
          path: `${PATH_RESTORE_PASSWORD}/:link`,
          element: <FinishPasswordRestoration />,
        },
        { path: PATH_NOTFOUND, element: <NotFound /> },
        { path: '*', element: <Navigate to={PATH_NOTFOUND} /> },
      ],
    },
    {
      path: `${ROUTE_PUBLIC_DICTIONARY}/:dictionaryId`,
      element: <PublicDictionary />,
    },
    {
      path: `${ROUTE_PUBLIC_DEPT}/:token`,
      element: <PublicDept />,
    },
    {
      path: `${ROUTE_PUBLIC_SHARED_TODO}/:token`,
      element: <PublicSharedTodo />,
    },
    {
      path: ROUTE_MATERIAL_SHARED(),
      element: <MaterialPlayer />,
    },
    {
      path: ROUTE_COURSE_SHARED(),
      element: <CoursePlayerPublic />,
    },
    { path: '*', element: <Navigate to={PATH_NOTFOUND} replace /> },
  ])
}
